import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, formatTL } from "@/lib/api";
import { StatusBadge } from "./AdminDashboard";

const STATUSES = ["yeni", "hazirlaniyor", "kargoda", "tamamlandi", "iptal"];
const LABELS = { yeni: "Yeni", hazirlaniyor: "Hazırlanıyor", kargoda: "Kargoya Verildi", tamamlandi: "Tamamlandı", iptal: "İptal" };
const PAY = { havale: "Havale/EFT", kapida: "Kapıda Ödeme", whatsapp: "WhatsApp Onay" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = filter ? { status: filter } : {};
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data);
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading text-4xl">Siparişler</h1>
      <p className="text-larc-muted mt-1">Tüm sipariş ve durum yönetimi.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <FilterBtn active={!filter} onClick={() => setFilter("")} label="Tümü" />
        {STATUSES.map((s) => <FilterBtn key={s} active={filter === s} onClick={() => setFilter(s)} label={LABELS[s]} />)}
      </div>

      <div className="mt-6 bg-white border border-larc-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-larc-alt text-larc-muted">
            <tr>
              <th className="text-left px-6 py-3">Sipariş No</th>
              <th className="text-left px-6 py-3">Müşteri</th>
              <th className="text-left px-6 py-3">Telefon</th>
              <th className="text-left px-6 py-3">Tutar</th>
              <th className="text-left px-6 py-3">Ödeme</th>
              <th className="text-left px-6 py-3">Durum</th>
              <th className="text-left px-6 py-3">Tarih</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-10 text-center text-larc-muted">Sipariş yok</td></tr>
            ) : orders.map((o) => (
              <tr key={o.id} onClick={() => setSelected(o)} className="border-t border-larc-border cursor-pointer hover:bg-larc-alt/50" data-testid={`order-row-${o.order_number}`}>
                <td className="px-6 py-3 font-medium">{o.order_number}</td>
                <td className="px-6 py-3">{o.customer_name}</td>
                <td className="px-6 py-3 text-larc-muted">{o.phone}</td>
                <td className="px-6 py-3">{formatTL(o.total)}</td>
                <td className="px-6 py-3 text-larc-muted">{PAY[o.payment_method] || o.payment_method}</td>
                <td className="px-6 py-3"><StatusBadge status={o.status} /></td>
                <td className="px-6 py-3 text-larc-muted">{new Date(o.created_at).toLocaleString("tr-TR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <OrderDetail order={selected} onClose={() => setSelected(null)} onUpdated={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function FilterBtn({ active, onClick, label }) {
  return (
    <button onClick={onClick} className={`text-sm px-4 py-2 rounded-full border transition-colors ${active ? "bg-larc-ink text-white border-larc-ink" : "bg-white text-larc-ink border-larc-border hover:border-larc-gold"}`}>{label}</button>
  );
}

function OrderDetail({ order, onClose, onUpdated }) {
  const [status, setStatus] = useState(order.status);
  const [notes, setNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}`, { status, admin_notes: notes });
      toast.success("Sipariş güncellendi");
      onUpdated();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="order-detail">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-larc-border">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-larc-gold">Sipariş</div>
            <h3 className="font-heading text-3xl">{order.order_number}</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div>
            <SectionTitle>Müşteri</SectionTitle>
            <Info label="Ad Soyad" value={order.customer_name} />
            <Info label="Telefon" value={order.phone} />
            <Info label="E-posta" value={order.email || "—"} />
            <Info label="Şehir" value={order.city} />
            <Info label="Adres" value={order.address} />
            {order.notes && <Info label="Sipariş Notu" value={order.notes} />}
          </div>
          <div>
            <SectionTitle>Sipariş</SectionTitle>
            <Info label="Tarih" value={new Date(order.created_at).toLocaleString("tr-TR")} />
            <Info label="Ödeme" value={({ havale: "Havale/EFT", kapida: "Kapıda Ödeme", whatsapp: "WhatsApp Onay" })[order.payment_method] || order.payment_method} />
            <Info label="Toplam" value={formatTL(order.total)} />
          </div>
        </div>

        <div className="px-6 pb-2">
          <SectionTitle>Ürünler</SectionTitle>
          <div className="space-y-2">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3 bg-larc-alt rounded-xl px-4 py-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white shrink-0">{i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{i.name}</div>
                  <div className="text-xs text-larc-muted">{i.quantity} × {formatTL(i.price)}</div>
                </div>
                <div className="text-sm">{formatTL(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-4 border-t border-larc-border mt-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">Durum</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm bg-white outline-none focus:border-larc-gold" data-testid="order-status-select">
              {STATUSES.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-larc-muted">Admin Notu</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 w-full border border-larc-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-larc-gold" />
          </label>
        </div>
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-larc-border text-sm">Kapat</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-larc-ink text-white text-sm hover:bg-larc-gold transition-colors disabled:opacity-50" data-testid="order-save">
            {saving ? "Kaydediliyor…" : "Güncelle"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) { return <div className="text-xs uppercase tracking-[0.18em] text-larc-gold mb-3">{children}</div>; }
function Info({ label, value }) {
  return (
    <div className="mb-2">
      <div className="text-xs text-larc-muted">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
